import { useStore } from '../context/ProductStore';
import { Link } from 'react-router-dom';
import {
  Package, TrendingUp, Tag, AlertCircle,
  PlusCircle, Eye, Star, ShoppingBag,
  ArrowUpRight, BarChart2, Layers
} from 'lucide-react';
import './Dashboard.css';

const RECENT_COUNT = 6;

export default function Dashboard() {
  const { products, stats, coupons, categories = [], maintenanceMode, saveMaintenanceMode } = useStore();

  const recent = [...products]
    .sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded))
    .slice(0, RECENT_COUNT);

  const topRated = [...products]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 5);

  const statCards = [
    { label: 'Total Products', value: stats.total,      icon: <Package size={20} />,     color: 'blue',   change: '+3 this week'  },
    { label: 'Published',      value: stats.published,  icon: <Eye size={20} />,          color: 'green',  change: `${stats.draft} drafts`  },
    { label: 'On Sale',        value: stats.onSale,     icon: <TrendingUp size={20} />,   color: 'yellow', change: 'Active offers'  },
    { label: 'Out of Stock',   value: stats.outofstock, icon: <AlertCircle size={20} />,  color: 'red',    change: 'Needs restocking' },
    { label: 'Categories',     value: stats.categories, icon: <Layers size={20} />,       color: 'purple', change: 'Product groups'  },
    { label: 'Active Coupons', value: coupons.filter(c=>c.active).length, icon: <Tag size={20} />, color: 'orange', change: 'Live discount codes' },
  ];

  // Fake category distribution
  const catData = categories.slice(0, 6).map(cat => ({
    name: cat.name,
    emoji: cat.emoji,
    count: products.filter(p => p.category === cat.id).length,
    pct: Math.round((products.filter(p => p.category === cat.id).length / Math.max(products.length, 1)) * 100),
  }));

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-sub">Welcome back! Here's what's happening with your store.</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className={`admin-btn ${maintenanceMode ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => saveMaintenanceMode(!maintenanceMode)}
            style={maintenanceMode ? { background: '#f59e0b', borderColor: '#f59e0b' } : {}}
          >
            {maintenanceMode ? '🔥 Pause Mode: ON' : '🚀 Pause Mode: OFF'}
          </button>
          <Link to="/admin/add" className="admin-btn admin-btn-primary">
            <PlusCircle size={16} /> Add Product
          </Link>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="dash-stats-grid">
        {statCards.map((s, i) => (
          <div key={i} className={`dash-stat-card dash-stat-${s.color}`}>
            <div className="dash-stat-icon">{s.icon}</div>
            <div className="dash-stat-info">
              <span className="dash-stat-value">{s.value}</span>
              <span className="dash-stat-label">{s.label}</span>
              <span className="dash-stat-change">{s.change}</span>
            </div>
            <ArrowUpRight size={16} className="dash-stat-arrow" />
          </div>
        ))}
      </div>

      {/* Main row */}
      <div className="dash-main-row">
        {/* Recent products */}
        <div className="admin-card dash-recent">
          <div className="dash-section-header">
            <h3 className="dash-section-title"><Package size={16} /> Recent Products</h3>
            <Link to="/admin/products" className="admin-btn admin-btn-secondary admin-btn-sm">View All</Link>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recent.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div className="dash-product-cell">
                        <img
                          src={p.images?.[0]?.url || p.image}
                          alt={p.name}
                          className="dash-product-img"
                          onError={e => e.target.src = 'https://via.placeholder.com/40x40/f3f4f6/9ca3af?text=P'}
                        />
                        <div>
                          <p className="dash-product-name">{p.name}</p>
                          <p className="dash-product-sku">SKU: {p.sku}</p>
                        </div>
                      </div>
                    </td>
                    <td><span className="dash-cat-chip">{p.category}</span></td>
                    <td>
                      <span className="dash-price">₹{p.price}</span>
                      {p.price < p.mrp && <span className="dash-mrp">₹{p.mrp}</span>}
                    </td>
                    <td>
                      <span className={`status-badge status-${p.stock}`}>
                        {p.stock === 'instock' ? 'In Stock' : 'Out of Stock'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${p.status}`}>{p.status}</span>
                    </td>
                    <td>
                      <Link to={`/admin/edit/${p.id}`} className="admin-btn admin-btn-secondary admin-btn-sm">Edit</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right col */}
        <div className="dash-right-col">
          {/* Category breakdown */}
          <div className="admin-card">
            <div className="dash-section-header">
              <h3 className="dash-section-title"><BarChart2 size={16} /> Category Breakdown</h3>
            </div>
            <div className="dash-cat-list">
              {catData.map((cat, i) => (
                <div key={i} className="dash-cat-row">
                  <span className="dash-cat-emoji">{cat.emoji}</span>
                  <div className="dash-cat-info">
                    <div className="dash-cat-name-row">
                      <span className="dash-cat-name">{cat.name}</span>
                      <span className="dash-cat-count">{cat.count}</span>
                    </div>
                    <div className="dash-cat-bar">
                      <div className="dash-cat-bar-fill" style={{ width: `${Math.max(cat.pct, 5)}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Rated */}
          <div className="admin-card" style={{ marginTop: '20px' }}>
            <div className="dash-section-header">
              <h3 className="dash-section-title"><Star size={16} /> Top Rated</h3>
            </div>
            <div className="dash-top-list">
              {topRated.map((p, i) => (
                <div key={p.id} className="dash-top-item">
                  <span className="dash-top-rank">#{i+1}</span>
                  <img
                    src={p.images?.[0]?.url || p.image}
                    alt={p.name}
                    className="dash-top-img"
                    onError={e => e.target.src = 'https://via.placeholder.com/32x32/f3f4f6/9ca3af?text=P'}
                  />
                  <div className="dash-top-info">
                    <p className="dash-top-name">{p.name}</p>
                    <p className="dash-top-rating">⭐ {p.rating} ({p.reviews?.toLocaleString()})</p>
                  </div>
                  <span className="dash-top-price">₹{p.price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="dash-quick-actions">
        <h3 className="dash-section-title" style={{ marginBottom: '16px' }}>⚡ Quick Actions</h3>
        <div className="dash-actions-grid">
          {[
            { label: 'Add Product',   icon: <PlusCircle size={20} />,  to: '/admin/add',      color: 'red'    },
            { label: 'All Products',  icon: <Package size={20} />,     to: '/admin/products', color: 'blue'   },
            { label: 'Manage Coupons',icon: <Tag size={20} />,          to: '/admin/coupons',  color: 'yellow' },
            { label: 'View Store',    icon: <ShoppingBag size={20} />, to: '/',               color: 'green'  },
          ].map(a => (
            <Link key={a.label} to={a.to} className={`dash-action-card dash-action-${a.color}`}>
              <span className="dash-action-icon">{a.icon}</span>
              <span className="dash-action-label">{a.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
